/**
 * Mídia autenticada do CHAT (v8 Etapa 3b): a rota staff de serving
 * (`/chats/:id/messages/:mid/media`) exige Bearer; pedida em modo JSON
 * (`?format=json`) responde 200 {url, expiresIn} com a URL presignada do
 * storage. O hook resolve essa URL com react-query (cache por mediaUrl, com
 * staleTime abaixo do TTL de 10min da presignada) e o componente usa a URL
 * DIRETO como src de <img>/<audio>/<video> ou href de download — elementos de
 * mídia e navegação não sofrem bloqueio CORS, o browser faz streaming com
 * Range e usa o cache HTTP dele (nada de fetch+blob bufferizando o arquivo
 * inteiro em memória; era isso que quebrava em produção, já que o S3 não
 * manda headers CORS).
 */
import { useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api-client';

/** O TTL da presignada é 10min no servidor — renova antes de expirar. */
const MEDIA_URL_STALE_MS = 8 * 60_000;

export interface ChatMediaState {
  /** URL presignada pronta para src/href (null enquanto carrega/erro) */
  url: string | null;
  loading: boolean;
  error: boolean;
}

/** mediaUrl (caminho relativo da API) → URL presignada, com loading/erro. */
export function useChatMedia(mediaUrl: string | null): ChatMediaState {
  const query = useQuery({
    queryKey: ['erp', 'chat-media', mediaUrl] as const,
    enabled: mediaUrl !== null,
    staleTime: MEDIA_URL_STALE_MS,
    gcTime: MEDIA_URL_STALE_MS,
    queryFn: async ({ signal }) => {
      const res = await api<{ url: string; expiresIn: number }>(mediaUrl as string, {
        query: { format: 'json' },
        signal,
      });
      if (res.error) throw res.error;
      return res.data;
    },
  });

  return {
    url: query.data?.url ?? null,
    loading: mediaUrl !== null && query.isPending,
    error: query.isError,
  };
}

/**
 * Baixar a mídia como ANEXO (spec 013 — nota de voz não tinha como baixar):
 * `?download=1` faz a API presignar com Content-Disposition attachment e nome
 * derivado do tipo (`audio-<id>.ogg`). A navegação para a presignada dispara o
 * download sem sair da página — o S3 responde anexo, não documento.
 */
export async function downloadChatMedia(mediaUrl: string): Promise<void> {
  const res = await api<{ url: string; expiresIn: number }>(mediaUrl, {
    query: { format: 'json', download: '1' },
  });
  if (res.error) throw res.error;
  const a = document.createElement('a');
  a.href = res.data.url;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
}
